import { camelCase, get } from 'lodash';
import { useI18n } from 'vue-i18n';

import {
  arr,
  AssetContainerEventType,
  getAssetContainerStatus as coreGetAssetContainerStatus
} from '@nova/core';
import {
  getAssetContainerStatusColor as coreGetAssetContainerStatusColor,
  getAssetContainerStatusIcon as coreGetAssetContainerStatusIcon
} from '@satellite/helpers/yardTaskUtils';

import type { IAssetContainerEvent, Maybe } from '@nova/core';
import type { YardAssetDetails } from '@satellite/composables/yardAssetPresenterV2Shared';

/** Minimal event shape the status rules need; list rows and DTOs carry slimmer events. */
export interface YardAssetStatusEvent {
  eventType?: Maybe<string>;
  createDateTime?: Maybe<string | Date>;
}

/** An asset container, or just its events: either is enough to resolve a status. */
export type YardAssetStatusInput = Maybe<
  | YardAssetDetails
  | YardAssetStatusEvent[]
  | { assetContainerEvents?: Maybe<YardAssetStatusEvent[]> }
>;

/** Skipped when looking back for the status to display while an asset is in transit. */
const STATUSES_NOT_PRESERVED_THROUGH_TRANSIT = new Set<string>([
  AssetContainerEventType.InTransit,
  AssetContainerEventType.Missing,
  AssetContainerEventType.Departed
]);

export function toAssetContainerEvents(input: YardAssetStatusInput): IAssetContainerEvent[] {
  const events = Array.isArray(input) ? input : get(input, 'assetContainerEvents');

  return arr.toArray(events) as IAssetContainerEvent[];
}

export function getRawAssetContainerStatus(input: YardAssetStatusInput): string | null {
  return coreGetAssetContainerStatus(toAssetContainerEvents(input));
}

/** In Transit is a condition, not a status: the asset keeps the work status it had when it started. */
export function getAssetContainerDisplayStatus(input: YardAssetStatusInput): string | null {
  const events = toAssetContainerEvents(input);
  const rawStatus = coreGetAssetContainerStatus(events);

  if (rawStatus !== AssetContainerEventType.InTransit) {
    return rawStatus;
  }

  const statusBeforeTransit = coreGetAssetContainerStatus(
    events.filter(event => !STATUSES_NOT_PRESERVED_THROUGH_TRANSIT.has(event?.eventType))
  );

  return statusBeforeTransit ?? AssetContainerEventType.Arrived;
}

/**
 * Single source of truth for an asset container's status: the raw status, the status to display,
 * the in-transit condition, and the label, icon and colour of each.
 */
export function useYardAssetStatus() {
  const { t } = useI18n();

  const getRawStatus = (asset: YardAssetStatusInput): string | null =>
    getRawAssetContainerStatus(asset);

  const getDisplayStatus = (asset: YardAssetStatusInput): string | null =>
    getAssetContainerDisplayStatus(asset);

  const isAssetInTransit = (asset: YardAssetStatusInput): boolean =>
    getRawStatus(asset) === AssetContainerEventType.InTransit;

  const getStatusLabel = (status: Maybe<string>): string | null => {
    if (!status) {
      return null;
    }

    return t(`core.yard.assetContainerEvent.types.${camelCase(status)}`, status);
  };

  const getRawStatusLabel = (asset: YardAssetStatusInput): string | null =>
    getStatusLabel(getRawStatus(asset));

  const getDisplayStatusLabel = (asset: YardAssetStatusInput): string | null =>
    getStatusLabel(getDisplayStatus(asset));

  const getRawStatusIcon = (asset: YardAssetStatusInput): string =>
    coreGetAssetContainerStatusIcon(getRawStatus(asset) ?? '');

  const getDisplayStatusIcon = (asset: YardAssetStatusInput): string =>
    coreGetAssetContainerStatusIcon(getDisplayStatus(asset) ?? '');

  const getRawStatusColor = (asset: YardAssetStatusInput): string | undefined =>
    coreGetAssetContainerStatusColor(getRawStatus(asset) ?? '');

  const getDisplayStatusColor = (asset: YardAssetStatusInput): string | undefined =>
    coreGetAssetContainerStatusColor(getDisplayStatus(asset) ?? '');

  return {
    getRawStatus,
    getDisplayStatus,
    isAssetInTransit,
    getStatusLabel,
    getRawStatusLabel,
    getDisplayStatusLabel,
    getRawStatusIcon,
    getDisplayStatusIcon,
    getRawStatusColor,
    getDisplayStatusColor
  };
}
